import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight, ComparisonTable } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-loan-requirements";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "DSCR loan requirements",
  "DSCR loan credit score requirement",
  "DSCR loan down payment",
  "minimum DSCR ratio",
  "DSCR loan reserves requirement",
  "DSCR loan eligible property types",
  "DSCR loan LLC requirement",
  "how to qualify for a DSCR loan",
  "DSCR loan documents needed",
  "DSCR loan seasoning requirements",
  "DSCR loan appraisal 1007",
  "DSCR loan minimum loan amount",
  "DSCR loan first time investor",
  "DSCR loan requirements Florida",
].join(", ");

const FAQS = [
  {
    q: "What credit score do you need for a DSCR loan?",
    a: "660 is the general program floor. 700–719 is mid-tier pricing and 720+ reaches the best tier. Scores in the 620–659 range can work with 30% or more down, stronger reserves, and compensating factors.",
  },
  {
    q: "What is the minimum DSCR ratio?",
    a: "Most programs want 1.00 or better. Our expanded matrix goes down to a 0.80 floor with adjustments, and No-Ratio options exist below that with a larger down payment and rate premium.",
  },
  {
    q: "How much down payment does a DSCR loan require?",
    a: "Typically 20–25%. The standard maximum LTV is 80% on purchase and limited cash-out, and 75% on cash-out at the strongest tier; 85% is exception-only at 740+ FICO on a single-family rental.",
  },
  {
    q: "Do I need reserves?",
    a: "Yes — generally 1 to 6 months of PITIA in a verified account at closing, scaled by loan size, leverage, and DSCR. Larger loans and higher LTVs require more.",
  },
  {
    q: "Do I need an LLC for a DSCR loan?",
    a: "It is not required, but entity vesting is standard, free of a pricing premium, and preferred by most investors. You can also close in your personal name on most programs.",
  },
  {
    q: "Can a first-time investor get a DSCR loan?",
    a: "Yes on many programs, though some require prior ownership or property-management experience at higher leverage. First-time investors typically see slightly lower maximum LTV.",
  },
  {
    q: "What documents are required?",
    a: "Entity documents, government ID, an insurance quote, a lease or rent schedule, bank statements for reserves, and a purchase contract or existing mortgage statement. No tax returns, W-2s, or pay stubs.",
  },
  {
    q: "Is there a minimum loan amount?",
    a: "Our DSCR programs generally start at $75,000–$100,000 and run to $3,500,000. Very small loan balances price higher because fixed costs are spread over less principal.",
  },
  {
    q: "Can I qualify for a DSCR loan with no credit history?",
    a: "Thin-file or no-score borrowers generally don't fit standard DSCR guidelines, which require an established FICO score. Some programs offer alternative-credit review, but expect a higher down payment and additional documentation.",
  },
  {
    q: "Do DSCR loans require a business entity to already exist?",
    a: "No — you can close in your personal name on most programs, or form an LLC before closing if you prefer entity vesting, which is standard practice among investors and carries no pricing premium.",
  },
  {
    q: "Is prior landlord experience required?",
    a: "Not on most programs, though first-time investors may see slightly lower maximum leverage. Prior ownership or property-management experience can unlock the higher end of a program's LTV range.",
  },
  {
    q: "What property condition requirements apply?",
    a: "The property must be safe, sound, and habitable per the appraisal, with no significant deferred maintenance. Properties needing substantial rehab typically need to be renovated first or financed through a separate bridge product.",
  },
];

const REQUIREMENTS: { title: string; body: string }[] = [
  { title: "Property type", body: "1–4 unit non-owner-occupied residential, plus 5–8 unit residential on select programs. Warrantable and non-warrantable condos, townhomes, and PUDs are eligible; condotels and rural land carry restrictions." },
  { title: "DSCR ratio", body: "Rent ÷ PITIA. 1.00+ for full leverage, 1.25+ for best pricing, 0.80 floor on the expanded matrix, and No-Ratio options below that." },
  { title: "Down payment / LTV", body: "20–25% down is typical. Up to 80% LTV on purchase and limited cash-out; 75% on cash-out. 85% is exception-only at 740+ FICO on an SFR." },
  { title: "Credit score", body: "660 minimum on most programs; 700+ improves pricing and leverage; 720+ is the best-pricing tier." },
  { title: "Reserves", body: "1–6 months of PITIA verified at closing, scaled by loan size, LTV, and DSCR." },
  { title: "Appraisal", body: "Full interior appraisal plus a rent schedule — Form 1007 for single units or Form 1025 for 2–4 units. Market rent is used when it is lower than the in-place lease." },
  { title: "Vesting & ownership", body: "LLC, LP, or corporation vesting is standard and carries no premium. Personal-name vesting is available on most programs." },
  { title: "Occupancy & purpose", body: "Business purpose only. The property must be non-owner-occupied and held as an investment — never a primary or second home." },
];

export const Route = createFileRoute("/dscr-loan-requirements")({
  head: () =>
    buildHead({
      title: "DSCR Loan Requirements: Credit, DSCR, Down Payment & Reserves",
      description:
        "Everything a DSCR loan requires — 660+ FICO, 1.00 DSCR for full leverage, 20–25% down, 1–6 months reserves, eligible property types, and the exact document list.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "DSCR Loan Requirements", url: URL },
      ],
      service: { name: "DSCR Loan Qualification Requirements" },
      faqs: FAQS,
    }),
  component: DscrRequirements,
});

function DscrRequirements() {
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR Requirements">
      <PageHero
        eyebrow="DSCR · Requirements"
        title="DSCR loan requirements"
        subtitle="A DSCR loan is underwritten against the property, not your paystubs. That means the qualification list is short — but every item on it is firm. Here is exactly what a rental-property DSCR file needs to clear."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <PageEssentials hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Requirements" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Snapshot" },
          { href: "#requirements", label: "Requirement checklist" },
          { href: "#credit-tiers", label: "Credit tiers" },
          { href: "#documents", label: "Documents" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "DSCR qualification is property-first: the rent versus PITIA drives approval, not W-2s, pay stubs, or tax returns.",
          "Typical baseline is 660+ FICO, 20–25% down (up to 80% LTV standard), and a DSCR at or above 1.00.",
          "Reserves of roughly 3–6 months of PITIA are standard; higher leverage and lower ratios ask for more.",
          "Properties must be non-owner-occupied 1–4 unit rentals, condos, or eligible small multifamily held for business purposes.",
          "Requirements shown are general program guidelines, not an approval or commitment to lend.",
        ]}
      />

      <Section id="snapshot">
        <StatRow
          items={[
            { label: "Min FICO", value: "660" },
            { label: "Min DSCR", value: "0.80 floor" },
            { label: "Max LTV", value: "80%" },
            { label: "Reserves", value: "1–6 mo PITIA" },
          ]}
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          Debt Service Coverage Ratio is gross monthly rent divided by PITIA — principal, interest, taxes, insurance,
          and association dues. Clear the ratio, the credit floor, and the reserve requirement and the file works,
          regardless of what your tax returns show.
        </p>
      </Section>

      <div className="bg-muted/40">
        <Section id="requirements">
          <SectionHeader eyebrow="The checklist" title="Every DSCR requirement, item by item" />
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {REQUIREMENTS.map((r) => (
              <div key={r.title} className="rounded-xl border border-border bg-card p-5">
                <div className="font-display text-base font-semibold text-foreground">{r.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow={"Live pricing · Requirements"}
        heading={"How DSCR Requirements Change the Rate You're Quoted"}
        paragraphs={[
            "Requirements and pricing are part of the same conversation. Credit profile, coverage ratio, leverage, property type and prepayment structure are all inputs investors price on, which is why two files on the same property can come back differently.",
            "The live options below show what the pricing engine returned most recently for a qualifying scenario. Change a single input in the rate center — leverage, FICO band, or prepayment term — and you can see how much each requirement is actually worth in rate.",
        ]}
        faqs={[
            { q: "Which requirement moves my rate the most?", a: "We do not rank them, because the effect of each input varies by investor and scenario. Leverage, credit profile, coverage ratio, property type and prepayment structure are all priced. The practical way to see what a change is worth on your file is to re-price the scenario with that one input changed." },
            { q: "Do I need to meet every requirement to get a quote?", a: "No. We can price the scenario as it stands and show where it lands, then show what changes if a specific input improves." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "Test requirement changes in the rate center" },
            { to: "/dscr-loan-rates", label: "See DSCR pricing tiers by DSCR, LTV and FICO" },
            { to: "/apply", label: "Start a DSCR scenario review" },
        ]}
      />

      <Section id="credit-tiers">
        <SectionHeader
          eyebrow="Credit & leverage"
          title="How FICO and DSCR set your maximum leverage"
          intro="Leverage is a grid, not a single number: your credit tier and your ratio together decide how much of the purchase price a lender will finance."
        />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">FICO</th>
                <th className="px-4 py-3">DSCR ≥ 1.10</th>
                <th className="px-4 py-3">DSCR ≥ 1.00</th>
                <th className="px-4 py-3">DSCR ≥ 0.80</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["720+", "Up to 80% LTV", "Up to 80% LTV", "Up to 75% LTV"],
                ["700–719", "Up to 80% LTV", "Up to 70% LTV", "Up to 70% LTV"],
                ["680–699", "Up to 80% LTV", "Up to 75% LTV", "Up to 75% LTV"],
                ["660–679", "Up to 75% LTV", "Up to 75% LTV", "Up to 70% LTV"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                  <td className="px-4 py-3 text-brand">{row[1]}</td>
                  <td className="px-4 py-3 text-foreground">{row[2]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Illustrative maximums for 1–4 unit purchase and limited cash-out at loan amounts up to $1MM. Larger balances,
          cash-out, short-term rentals, and non-warrantable condos reduce these figures. Not a commitment to lend.
        </p>
      </Section>

      <ComparisonTable
        productLabel="DSCR"
        comparison={{
          intro: "DSCR requirements side by side with a conventional investor loan and hard money. General program ranges, not an offer or commitment to lend.",
          columns: ["DSCR Loan", "Conventional Investor", "Hard Money"],
          rows: [
            { feature: "How you qualify", values: ["Rent ÷ PITIA", "Personal income / DTI", "Property value + exit"] },
            { feature: "Income docs", values: ["None", "2 yrs tax returns + W-2s", "None"] },
            { feature: "Min credit score", values: ["660 typical", "620+ typical", "Flexible"] },
            { feature: "Down payment", values: ["20–25%", "15–25%", "20–35%"] },
            { feature: "Reserves", values: ["1–6 mo PITIA", "Varies by count of properties", "Varies"] },
            { feature: "LLC vesting", values: ["Yes — no premium", "Personal only", "Yes"] },
            { feature: "Term", values: ["30-yr fixed / ARM / IO", "30-yr fixed", "6–24 months"] },
          ],
        }}
      />

      <Section id="documents">
        <SectionHeader eyebrow="Paperwork" title="Documents you will be asked for" />
        <div className="mt-8">
          <FeatureGrid
            items={[
              { title: "Entity documents", body: "Articles of organization, operating agreement, EIN letter, and certificate of good standing." },
              { title: "Identification", body: "Government photo ID for each guarantor. Foreign nationals use passport and visa documentation." },
              { title: "Property income", body: "Executed lease or rent roll. Where there is no lease, the appraiser's Form 1007 market rent is used." },
              { title: "Insurance", body: "Landlord (DP-3) policy quote with required coverage limits and mortgagee clause." },
            ]}
          />
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {[
            ["Reserves verification", "Two months of business or personal bank statements showing the required PITIA reserves."],
            ["Transaction documents", "Purchase contract for a purchase, or the current mortgage statement and payoff for a refinance."],
            ["Track record (when applicable)", "A schedule of real estate owned if you already hold rentals — used for experience tiers and portfolio pricing."],
            ["What you will NOT provide", "No tax returns, no W-2s, no pay stubs, and no personal debt-to-income calculation."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-5">
              <div className="font-display text-base font-semibold text-foreground">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <ProsCons
        title="Where DSCR requirements help you — and where they bind"
        pros={[
          "No income documentation and no DTI ceiling",
          "Unlimited financed properties",
          "Entity vesting at no pricing premium",
          "Self-employed and 1099 investors are not penalized",
        ]}
        cons={[
          "The property must carry itself or you add down payment",
          "Reserves must be verified and seasoned",
          "Business purpose only — no owner occupancy at any point",
          "Appraisal and rent schedule drive the file, so a weak 1007 can reprice the deal",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Not sure whether your deal clears the grid?"
        body="Send the address, rent, and credit range — we'll tell you which tier it lands in before you spend a dollar on an appraisal."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="DSCR requirement questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Guidelines shown are illustrative and vary by lender, property, and state. Eligibility, terms, and
          availability are subject to underwriting and approval. Not a commitment to lend. Business-purpose loans only.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-requirements" />

      <CTABand
        title="See whether your property qualifies"
        body="A short pre-qualification request is all it takes to get a scenario review."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
